import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {OptionService} from "../option/option.service";
import {Donation} from "../donation/donation";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Bonus} from "./bonus";

/**
 * global variable for showing a bonus element
 */
let __twingleBonusId: Bonus = null;

@Component({
    selector: 'app-bonus',
    templateUrl: './bonus.component.html',
})
export class BonusComponent implements OnInit, OnDestroy, OnChanges {
    /**
     * the donation object for the user
     */
    @Input() donation: Donation;

    /**
     * the amount is given extra, to check for changes here
     */
    @Input() amount: number;

    /**
     * the donation rhythm
     */
    @Input() rhythm: string;

    /**
     * Form for validation of the selection
     */
    @Input() form: FormGroup;

    /**
     * current step when entering the component
     */
    @Input() step: string;

    /**
     * event emitter if the step is changed
     */
    @Output() stepChange: EventEmitter<string> = new EventEmitter();

    /**
     * value if no bonus is selected
     */
    readonly bonusNoneOption: string = 'none';

    /**
     * step for showing the details
     */
    readonly bonusStepDetails: string = 'bonus-details';

    /**
     * toggle for this component to show content or not
     */
    hasBonus: boolean = false;

    /**
     * toggle for showing the details of a bonus
     */
    showDetails: Bonus = null;

    /**
     * list of available bonus
     */
    bonus: Bonus[];

    /**
     * some classes for the single columns to optimize layout
     */
    columClasses: string;

    /**
     * constructor
     * @param optionService
     */
    constructor(private optionService: OptionService) {}

    /**
     * @inheritDoc
     */
    ngOnInit() {
        // add element to form, but disabled to enable it later
        this.form.addControl('bonus', new FormControl('', [Validators.required]));
        (<FormControl>this.form.controls['bonus']).disable({ onlySelf: true });

        // get bonus data if exists
        this.updateBonusList();

        if(typeof __twingleBonusId !== "undefined" && this.step === this.bonusStepDetails) {
            // show details
            this.showDetails = __twingleBonusId;
        }
        else {
            this.showDetails = null;
            __twingleBonusId = null;
        }
    }

    /**
     * @inheritDoc
     */
    ngOnDestroy(): any {
        // disable all validators on the form
        this.form.removeControl('bonus');
    }

    /**
     * UPdates the list of available bonus
     */
    updateBonusList(): void {
        this.bonus = this.optionService.getBonus().filter((bonus: Bonus) => {
            // filter for min amount
            if(bonus.min_amount !== null && this.amount < bonus.min_amount) {
                return false;
            }

            // filter for recurring
            return !(bonus.is_recurring === true && this.rhythm === null);
        });

        // check if the bonus component should be visible
        this.hasBonus = this.optionService.getOption('has_bonus') === true && this.bonus.length > 0;

        // enable/disable form element
        setTimeout(() => {
            if(this.hasBonus === true) {
                // preset bonus
                const preselection = this.optionService.getOption('bonus_preset');
                if(preselection && !this.donation.bonus) {
                    this.donation.bonus = preselection;
                }

                // enable control element
                (<FormControl>this.form.controls['bonus']).enable({ onlySelf: true });

                // set current value
                (<FormControl>this.form.controls['bonus']).setValue(this.donation.bonus);

                // check if there is already a selection and if this is still
                if(this.bonus.findIndex(bonus => bonus.slug === this.donation.bonus) === -1 && this.donation.bonus !== this.bonusNoneOption) {
                    this.donation.bonus = null;
                    (<FormControl>this.form.controls['bonus']).setValue(null);
                }
                (<FormControl>this.form.controls['bonus']).updateValueAndValidity();
            }
            else if(this.form.controls.hasOwnProperty('bonus')){
                // disable the control element
                (<FormControl>this.form.controls['bonus']).disable();
            }
        }, 0);

        // set the column classes
        switch(this.bonus.length) {
            case 1:
                this.columClasses = 'small-12';
                break;
            case 2:
                this.columClasses = 'small-6';
                break;
            case 3:
                this.columClasses = 'small-4';
                break;
            default:
                this.columClasses = 'small-6 medium-3';
                break;
        }
    }

    /**
     * @inheritDoc
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {
        if((changes.hasOwnProperty('amount') && changes.amount.firstChange !== true) || (changes.hasOwnProperty('rhythm') && changes.rhythm.firstChange !== true)) {
            this.updateBonusList();
        }
    }

    /**
     * Select a bonus in the detialsview and move back to overview
     * @param bonus
     */
    selectBonus(bonus: Bonus): void {
        // set bonus on donation and form
        this.donation.bonus = bonus.slug;
        (<FormControl>this.form.controls['bonus']).setValue(this.donation.bonus);
        if(this.step !== 'start') {
            this.stepChange.emit('start');
        }
    }

    /**
     * Shows the details of a bonus.
     * @param bonus
     */
    onShowDetails(bonus: Bonus): void {
        // save the selected ID and show details step
        __twingleBonusId = bonus;
        this.stepChange.emit(this.bonusStepDetails);
    }

}
